import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { personFileService } from '../services/api';
import { Users, Building2, Search, Plus, RefreshCw, User, X } from 'lucide-react';

const PeopleDirectoryPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('people');
  const [q, setQ] = useState('');
  const [people, setPeople] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'people') {
        const r = await personFileService.listPeople(q);
        setPeople(r.people || []);
      } else {
        const r = await personFileService.listCompanies(q);
        setCompanies(r.companies || []);
      }
    } finally { setLoading(false); }
  }, [tab, q]);

  useEffect(() => { load(); }, [tab]);

  const submitCreate = async () => {
    setBusy(true); setErr('');
    try {
      if (tab === 'people') {
        if (!form.first_name) { setErr('First name is required'); setBusy(false); return; }
        const r = await personFileService.createPerson({ application_type: 'non_cdl', ...form });
        navigate(`/admin/person/${r.id}`);
      } else {
        if (!form.name) { setErr('Company name is required'); setBusy(false); return; }
        const r = await personFileService.createCompany({ company_type: 'customer', ...form });
        navigate(`/admin/company/${r.id}`);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="people-directory-page">
        <Breadcrumb items={[{ label: 'People & Companies' }]} />

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-gray-900">People &amp; Companies</h1>
          <button onClick={() => { setForm({}); setErr(''); setShowCreate(true); }} data-testid="create-record-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-900 text-white hover:bg-blue-800 text-sm font-medium">
            <Plus className="w-4 h-4" /> New {tab === 'people' ? 'Person' : 'Company'}
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab('people')} data-testid="tab-people"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'people' ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-600'}`}>
            <Users className="w-4 h-4" /> People
          </button>
          <button onClick={() => setTab('companies')} data-testid="tab-companies"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'companies' ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-600'}`}>
            <Building2 className="w-4 h-4" /> Companies
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex-1 min-w-[180px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder={`Search ${tab}…`} data-testid="directory-search"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={load} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Search
          </button>
          {tab === 'people' && (
            <button onClick={() => setAttentionOnly((v) => !v)} data-testid="attention-filter-btn"
              className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium ${attentionOnly ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <span className={`w-2 h-2 rounded-full ${attentionOnly ? 'bg-amber-500' : 'bg-gray-400'}`} /> Needs attention
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500"><RefreshCw className="w-8 h-8 animate-spin mx-auto" /></div>
          ) : tab === 'people' ? (
            (() => {
              const rows = attentionOnly ? people.filter((p) => p.workflow?.attention) : people;
              return rows.length === 0 ? <Empty label={attentionOnly ? 'No people need attention' : 'No people found'} /> : (
              <table className="w-full min-w-[640px]" data-testid="people-table">
                <thead><tr className="bg-gray-50 border-b text-left text-xs font-semibold text-gray-600 uppercase">
                  <th className="px-4 py-3">Name</th><th className="px-4 py-3">Stage / Readiness</th>
                  <th className="px-4 py-3">Next Action</th><th className="px-4 py-3">Outstanding</th>
                  <th className="px-4 py-3">Type</th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((p) => {
                    const w = p.workflow || {};
                    return (
                    <tr key={p.id} onClick={() => navigate(`/admin/person/${p.id}`)} data-testid={`person-row-${p.id}`}
                      className={`cursor-pointer ${w.attention ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-blue-50'}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          {w.attention && <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" title="Needs attention" />}
                          <User className="w-4 h-4 text-gray-400" />{p.full_name}
                        </div>
                        <span className="text-xs text-gray-400 ml-8">{p.email || 'no email'}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-gray-800">{w.stage_label || '—'}</span>
                        {w.ready_to_send === true && <span className="ml-2 px-1.5 py-0.5 rounded text-[11px] bg-green-50 text-green-700">ready</span>}
                        {w.ready_to_send === false && <span className="ml-2 px-1.5 py-0.5 rounded text-[11px] bg-red-50 text-red-700">not ready</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{w.next_action || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{w.outstanding_count > 0 ? w.outstanding_count : '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{p.application_type}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              );
            })()
          ) : (
            companies.length === 0 ? <Empty label="No companies found" /> : (
              <table className="w-full min-w-[560px]" data-testid="companies-table">
                <thead><tr className="bg-gray-50 border-b text-left text-xs font-semibold text-gray-600 uppercase">
                  <th className="px-4 py-3">Name</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Contacts</th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {companies.map((c) => (
                    <tr key={c.id} onClick={() => navigate(`/admin/company/${c.id}`)} data-testid={`company-row-${c.id}`}
                      className="hover:bg-blue-50 cursor-pointer">
                      <td className="px-4 py-3 font-medium text-gray-900 flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400" />{c.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{c.company_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{c.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{c.contact_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="create-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">New {tab === 'people' ? 'Person' : 'Company'}</h3>
              <button onClick={() => setShowCreate(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {err && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{err}</div>}
            <div className="space-y-3">
              {tab === 'people' ? (
                <>
                  <Field label="First Name *" onChange={(v) => setForm({ ...form, first_name: v })} testid="cp-first-name" />
                  <Field label="Last Name" onChange={(v) => setForm({ ...form, last_name: v })} testid="cp-last-name" />
                  <Field label="Email" onChange={(v) => setForm({ ...form, email: v })} testid="cp-email" />
                  <Field label="Phone" onChange={(v) => setForm({ ...form, phone: v })} testid="cp-phone" />
                  <div>
                    <label className="text-sm text-gray-600">Type</label>
                    <select onChange={(e) => setForm({ ...form, application_type: e.target.value })} data-testid="cp-type"
                      defaultValue="non_cdl" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      <option value="non_cdl">Non-CDL</option>
                      <option value="owner_operator">Owner Operator</option>
                      <option value="lease_on">Lease-On</option>
                      <option value="company_driver">Company Driver</option>
                      <option value="dispatch_only">Dispatch Only</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <Field label="Company Name *" onChange={(v) => setForm({ ...form, name: v })} testid="cc-name" />
                  <Field label="Legal Name" onChange={(v) => setForm({ ...form, legal_name: v })} testid="cc-legal-name" />
                  <Field label="Email" onChange={(v) => setForm({ ...form, email: v })} testid="cc-email" />
                  <div>
                    <label className="text-sm text-gray-600">Type</label>
                    <select onChange={(e) => setForm({ ...form, company_type: e.target.value })} data-testid="cc-type"
                      defaultValue="customer" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      <option value="customer">Customer</option>
                      <option value="carrier">Carrier</option>
                      <option value="vendor">Vendor</option>
                      <option value="partner">Partner</option>
                    </select>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={submitCreate} disabled={busy} data-testid="create-submit-btn"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-900 text-white hover:bg-blue-800 disabled:opacity-40">
                {busy ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const Field = ({ label, onChange, testid }) => (
  <div>
    <label className="text-sm text-gray-600">{label}</label>
    <input onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
  </div>
);

const Empty = ({ label }) => (
  <div className="p-8 text-center text-gray-500"><Users className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>{label}</p></div>
);

export default PeopleDirectoryPage;
